import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { ArrowUpRight, Phone } from "lucide-react";
import { PHONE } from "@/data";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fields = [
    { name: "name", label: t.contact.name, type: "text", placeholder: t.contact.namePh },
    { name: "email", label: t.contact.email, type: "email", placeholder: t.contact.emailPh },
    { name: "subject", label: t.contact.subject, type: "text", placeholder: t.contact.subjectPh },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.validation);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, subject: form.subject || "New enquiry" });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-32"
    >
      <Toaster theme="dark" position="bottom-right" richColors />
      <div className="grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <p className="font-satoshi font-bold text-xs tracking-[0.3em] uppercase text-accent mb-6 flex items-center gap-2">
            <span className="w-4 h-4 bg-brand" /> {t.contact.label}
          </p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-cabinet font-black uppercase text-7xl md:text-8xl tracking-tighter leading-[0.8]"
          >
            {t.contact.h1}
            <br />
            <span className="text-accent">{t.contact.h2}</span>
          </motion.h2>
          <p className="font-satoshi text-lg text-[#a1a1aa] mt-8 max-w-sm leading-relaxed">
            {t.contact.intro}
          </p>

          {/* Phone */}
          <a
            href={PHONE.href}
            data-testid="contact-phone"
            className="group mt-8 inline-flex items-center gap-4 border border-[#27272A] px-5 py-4 hover:border-brand transition-colors duration-200"
          >
            <span className="w-10 h-10 bg-brand text-white flex items-center justify-center shrink-0">
              <Phone size={18} />
            </span>
            <span className="flex flex-col">
              <span className="font-satoshi font-bold text-xs uppercase tracking-widest text-[#71717a]">
                {t.contact.phoneLabel}
              </span>
              <span className="font-cabinet font-bold text-xl tracking-tight group-hover:text-accent transition-colors">
                {PHONE.display}
              </span>
            </span>
          </a>
        </div>

        <form onSubmit={submit} data-testid="contact-form" className="md:col-span-7 flex flex-col gap-6">
          {fields.map((f) => (
            <div key={f.name} className="flex flex-col gap-2">
              <label className="font-satoshi font-bold text-xs uppercase tracking-widest text-[#71717a]">
                {f.label}
              </label>
              <input
                type={f.type}
                value={form[f.name]}
                onChange={update(f.name)}
                placeholder={f.placeholder}
                data-testid={`contact-${f.name}`}
                className="bg-transparent border border-[#27272A] px-4 py-4 font-satoshi text-lg text-white placeholder:text-[#52525b] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors duration-200"
              />
            </div>
          ))}
          <div className="flex flex-col gap-2">
            <label className="font-satoshi font-bold text-xs uppercase tracking-widest text-[#71717a]">
              {t.contact.message}
            </label>
            <textarea
              rows={5}
              value={form.message}
              onChange={update("message")}
              placeholder={t.contact.messagePh}
              data-testid="contact-message"
              className="bg-transparent border border-[#27272A] px-4 py-4 font-satoshi text-lg text-white placeholder:text-[#52525b] focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand transition-colors duration-200 resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="group mt-2 inline-flex items-center justify-center gap-4 font-cabinet font-black uppercase tracking-tight text-2xl md:text-3xl bg-brand text-white px-8 py-6 hover:bg-white hover:text-[#09090B] transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? t.contact.sending : t.contact.submit}
            <ArrowUpRight size={26} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </button>
        </form>
      </div>
    </section>
  );
}
